import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatsCard } from '@/components/shared/StatsCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Users, FileBarChart, DollarSign, TrendingUp, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { adminAPI, taxFilingAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_users: 0,
    total_tax_filings: 0,
    total_llc_formations: 0,
    total_revenue: 0,
    pending_tax_filings: 0
  });
  const [recentFilings, setRecentFilings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch admin statistics
        const statsData = await adminAPI.getAdminStats();
        setStats(statsData);
        
        // Fetch recent filings
        const filingsData = await adminAPI.getAllFilings({ limit: 5 });
        setRecentFilings(filingsData);
        
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load admin dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Admin Dashboard" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Admin Dashboard" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Admin Dashboard</h2>
          <p className="mt-1 text-muted-foreground">Platform overview and management</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          <StatsCard
            title="Total Users"
            value={stats.total_users.toString()}
            icon={Users}
            iconBgColor="bg-primary"
            trend="up"
            trendValue="+12%"
          />
          <StatsCard
            title="Tax Filings"
            value={stats.total_tax_filings.toString()}
            icon={FileBarChart}
            iconBgColor="bg-secondary"
            trend="up"
            trendValue="+8%"
          />
          <StatsCard
            title="LLC Formations"
            value={stats.total_llc_formations.toString()}
            icon={TrendingUp}
            iconBgColor="bg-accent"
            trend="up"
            trendValue="+3"
          />
          <StatsCard
            title="Total Revenue"
            value={`$${(stats.total_revenue / 1000).toFixed(1)}K`}
            icon={DollarSign}
            iconBgColor="bg-green-500"
            trend="up"
            trendValue="+15%"
          />
          <StatsCard
            title="Pending Filings"
            value={stats.pending_tax_filings.toString()}
            icon={AlertCircle}
            iconBgColor="bg-yellow-500"
            trend="down"
            trendValue="-2"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Filings */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Recent Filings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentFilings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No recent filings</p>
                ) : (
                  recentFilings.map((filing) => (
                    <div
                      key={filing.id}
                      className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/admin/filings/${filing.id}`)}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-foreground">
                          {filing.user_name || 'Unknown User'} - {filing.filing_type === 'business' ? 'Business' : 'Individual'} Tax Filing
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Tax Year: {filing.tax_year} • {filing.created_at ? new Date(filing.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={filing.status} />
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
